import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory} from 'react-router-dom/cjs/react-router-dom.min';
import { Row, Card, Breadcrumb, Col } from 'react-bootstrap';
import { Button, SaveButton } from '@rd-web-markets/shared/dist/util/buttons';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { UserService } from '@rd-web-markets/shared/dist/services/user.service';
import UserForm from '@components/users/UserForm';
import BreadcrumbsPortal from '@rd-web-markets/shared/dist/util/BreadcrumbsPortal';
import SidebarPortal from '@rd-web-markets/shared/dist/util/SidebarPortal';
import { useTranslation } from 'react-i18next';
import { setHasImportantUnsavedChanges } from '@rd-web-markets/shared/dist/store/features/unsavedChangesSlice';
import { USE_V2_DESIGN } from '@rd-web-markets/market/dist/constants';
import BackButton from '@rd-web-markets/shared/dist/util/buttons/BackButton';

const CreateUserPage = ({handleToaster}) => {
  const formId = 'create-user-form';
  const loggedUser = useSelector((state) => state.auth.user);
  const [user, setUser] = useState({company_users_attributes: [], company_users: []});
  const [loading, setLoading] = useState(false);
  const shouldNavigateAfterSave = useRef(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const hasUnsavedChanges = useSelector((state) => state.unsavedChanges.hasImportantUnsavedChanges);
  const { t } = useTranslation();
  const fromPathRef = useRef(history.location.state?.from);
  const inviteAllowed = window.MARKET_CONSTANTS.USERS_TYPE_WHO_CAN_INVITE_NEW_USERS.includes(loggedUser.account_type);
  const submitButtonText = inviteAllowed ? t('create_and_invite_user') : t('create_user');
  const submitButtonTextKey = inviteAllowed ? 'create_and_invite_user' : 'create_user';

  const handleChange = (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    dispatch(setHasImportantUnsavedChanges(true));
    setUser({
      ...user,
      [event.target.name]: value,
    });
  };


  const usersPagesAvailable = window.MARKET_CONSTANTS.USER_PAGE_AVAILABLE_FOR_ACCOUNT_TYPES.includes(loggedUser.account_type);

  const handleBack = useCallback(() => {
    if (!fromPathRef.current) {
      return history.push(`/${loggedUser.account_type}/users`);
    }

    // slice will remove the /de or /uk from the path
    return history.push(fromPathRef.current.slice(3));
  }, [history, fromPathRef, loggedUser.account_type]);

  useEffect(() => {
    if (shouldNavigateAfterSave.current && !hasUnsavedChanges) {
      shouldNavigateAfterSave.current = false;
      handleBack();
      window.location.reload();
    }
  }, [hasUnsavedChanges, handleBack]);

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const userToCreate = { ...user };
      if (loggedUser.account_type === 'super_consultant') {
        userToCreate.account_type = 'customer';
      }
      await UserService.create(userToCreate);
      shouldNavigateAfterSave.current = true;
      dispatch(setHasImportantUnsavedChanges(false));
    } catch(error) {
      dispatch(handleError(error));
      setLoading(false);
    }
  };
  
  return (
    <>
      {!USE_V2_DESIGN && <SidebarPortal headerItem={{link: 'companies', text: 'Companies'}} />}
      <BreadcrumbsPortal>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: `/${loggedUser.account_type}`}}>{t('home')}</Breadcrumb.Item>
          {usersPagesAvailable && <Breadcrumb.Item linkAs={Link} linkProps={{to: `/${loggedUser.account_type}/users`}}>{t('users')}</Breadcrumb.Item>}
          <Breadcrumb.Item active>New User</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbsPortal>

      {USE_V2_DESIGN ? (
        <Row className='justify-content-center mx-0 mt-4'>
          <Col md={10}>
            <Card>
              <Card.Body>
                <UserForm
                  inviteAllowed={inviteAllowed}
                  handleChange={handleChange}
                  formId={formId}
                  onSubmit={handleCreateUser}
                  user={user}
                  setUser={(newUser) => {
                    dispatch(setHasImportantUnsavedChanges(true));
                    setUser(newUser);
                  }}
                />
              </Card.Body>
              <Card.Footer>
                <Row className="modal-footer">
                  <BackButton onClick={handleBack} />
                  <Button
                    type="submit"
                    form={formId}
                    icon='save'
                    iconPosition={'left'}
                    loading={loading}
                    variant="primary">
                    {submitButtonText}
                  </Button>
                </Row>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      ) : (
        <Card>
          <Card.Body>
            <UserForm
              inviteAllowed={inviteAllowed}
              handleChange={handleChange}
              formId={formId}
              onSubmit={handleCreateUser}
              user={user}
              setUser={(newUser) => {
                dispatch(setHasImportantUnsavedChanges(true));
                setUser(newUser);
              }}
            />
          </Card.Body>
          <Card.Footer>
            <Row className="modal-footer">
              <Button variant="light" onClick={() => handleBack()}>
                <span className="material-icons md-18">chevron_left</span> {t('back')}
              </Button>
              <SaveButton
                form={formId}
                loading={loading}
                text={submitButtonTextKey}
              />
            </Row>
          </Card.Footer>
        </Card>
      )}
    </>
  );
}

export default CreateUserPage
